<template>
  <master-layout no-bottom-pad>
    <!-- <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
      <ion-refresher-content refreshing-spinner="lines"></ion-refresher-content>
    </ion-refresher> -->
    <div class="relative w-screen h-screen flex justify-center bg-grey-10">
      <PhotosModal :show="showModal" @close-modal="closeModal"></PhotosModal>

      <!-- Sticky header — always visible -->
      <!-- when side nav is shown: width = screen width - side nav-->
      <div
        ref="stickyHeader"
        class="photodrop-sticky-header fixed w-full lg:w-[calc(100vw-292px)] lg:right-0 px-6 pt-4 bg-grey-10 z-[4]"
        :class="{ '!pt-[60px]': isIos, '!pt-[max(16px,env(safe-area-inset-top))]': isAndroid }"
      >
        <div
          class="header-widgets flex flex-row mx-auto justify-between items-center"
          :class="
            tab.EVERYONE.displayText === selectedTab.displayText ? 'max-w-[964px]' : 'max-w-[616px]'
          "
        >
          <BackButton variant="bare" data-test-id="gensapp-photodrop-back" @click="back" />

          <div class="flex items-center gap-2">
            <!-- Download All button (For everyone tab only, hidden when selecting) -->
            <BaseButton
              v-if="!isSelectingPhotos && tab.EVERYONE.displayText === selectedTab.displayText"
              :solid-classes="`!rounded-[100px] items-center h-9 bg-lightblue text-white !text-[14px] !leading-[20px]`"
              :disabled="isDownloading"
              @click="handleDownloadAll"
            >
              {{ isDownloading ? downloadProgressText : 'Download All' }}
            </BaseButton>

            <!-- Download selected button (shown when selecting) -->
            <BaseButton
              v-if="isSelectingPhotos"
              :solid-classes="`!rounded-[100px] items-center h-9 bg-lightblue text-white !text-[14px] !leading-[20px]`"
              :disabled="selectedPhotos.length === 0 || isDownloading"
              :data-test-id="'gensapp-photodrop-download'"
              @click="
                handleDownload(
                  tab.EVERYONE.displayText === selectedTab.displayText ? 'everyone' : 'my',
                )
              "
            >
              {{ isDownloading ? downloadProgressText : `Download (${selectedPhotos.length})` }}
            </BaseButton>

            <!-- My QR, Select buttons -->
            <PhotosHeader
              :is-my-photos-tab="selectedTab.displayText === tab.MY.displayText"
              @open-modal="openModal"
            />
          </div>
        </div>

        <div class="header-tabs md:w-[436px] md:mx-auto flex space-x-2 mt-5 mx-4">
          <div
            v-for="(tab, index) in tabs"
            :key="`tickets-tab-${tab}-${index}`"
            class="flex justify-center items-center cursor-pointer w-full whitespace-nowrap text-[16px] xs:text-[20px] font-aktiv-grotesk text-center px-2 py-2 xs:px-4 rounded-t-[12px] md:whitespace-nowrap md:px-[42px] md:py-2 tab-card"
            :class="{
              'font-bold text-lightblue bg-white': tab.displayText === selectedTab.displayText,
              'bg-[#F8F9FB]': tab.displayText !== selectedTab.displayText,
              hidden: tab.displayText === 'For everyone' && isAbsentAlumni,
            }"
            :data-event-tracking="getDataEventTracking(tab)"
            :data-test-id="'gensapp-photodrop-tab-' + tab.displayText"
            @click="onSelectTab(tab)"
          >
            {{ tab.translatedText }}
          </div>
        </div>
      </div>

      <!-- Initial loader — sits below the header -->
      <div
        v-show="isInitialLoading"
        class="absolute inset-x-0 bottom-0 flex items-center justify-center bg-grey-10 z-[3]"
        :style="{ top: stickyHeaderHeight + 'px' }"
      >
        <LoadingState />
      </div>

      <!-- Content area — visible once initial load is done -->
      <div v-show="!isInitialLoading" class="w-full">
        <div class="flex flex-col w-full" :style="{ paddingTop: stickyHeaderHeight + 'px' }">
          <PhotoEveryoneDayTab
            v-if="tab.EVERYONE.displayText === selectedTab.displayText"
            class="fixed z-[4] left-0 lg:left-[292px]"
            :style="{ top: stickyHeaderBottom + 'px' }"
            ref="photoEveryOneDayTab"
          />
          <PhotoMyPhotoTypeTab
            v-if="tab.MY.displayText === selectedTab.displayText"
            class="fixed z-[4] left-0 lg:left-[292px]"
            :style="{ top: stickyHeaderBottom + 'px' }"
            ref="PhotoMyPhotoTypeTab"
            @refresh-photo="refreshMyPhoto"
          />
          <!-- Content loading dots (tab switch / re-fetch) -->
          <div
            v-show="contentLoading"
            class="flex justify-center py-8"
            :style="{ marginTop: subTabHeight + 'px' }"
          >
            <LoadingState />
          </div>

          <!-- Photo grid — hidden while content loading to avoid white flash -->
          <div
            v-show="!contentLoading"
            class="flex flex-col bg-white pb-12 md:pb-[32px] lg:w-[calc(100vw-292px)]"
            :style="{ marginTop: subTabHeight + 'px' }"
          >
            <div
              v-if="tab.MY.displayText === selectedTab.displayText"
              class="flex flex-col px-4 xs:px-6 pb-6 lg:p-0 md:mb-8"
            >
              <div
                class="shadow-ambient flex flex-col max-w-[616px] w-full mx-auto md:justify-center"
              >
                <div v-if="isPhotosEmpty">
                  <PhotosExpPhotosEmpty :tabType="selectedMyPhotoTypeTabGPCR" />
                </div>
                <div v-else>
                  <Gallery
                    v-for="(day, index) in photos"
                    :key="index"
                    :has-tooltip="index === 0"
                    v-bind="{ viewportWidth, ...day }"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="tab.EVERYONE.displayText === selectedTab.displayText"
              class="flex flex-col mx-2 pb-6 lg:p-0 md:mb-8"
            >
              <div
                class="shadow-ambient flex flex-col md:flex-row w-full md:max-w-[478px] lg:max-w-[964px] mx-auto md:justify-center"
              >
                <PhotosEveryone />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import platformMixin from '@/mixins/platformMixin';
import photoDownloadMixin from '@/mixins/photoDownloadMixin';
import { forEveryoneDaySlugs } from '@/utils/photodropPageSlugs';
import BaseButton from '../../components/common/BaseButton.vue';
import BackButton from '@/components/common/BackButton.vue';
import LoadingState from '@/components/common/LoadingState.vue';
import PhotosHeader from '@/components/Photo/PhotosHeader';
import Gallery from '@/components/Photo/Gallery';
import PhotosEveryone from '@/components/Photo/PhotosEveryone';
import PhotosExpPhotosEmpty from '@/components/Photo/PhotosExpPhotosEmpty';
import PhotosModal from '@/components/Photo/PhotosModal';
import PhotoEveryoneDayTab from '@/components/Photo/PhotoEveryoneDayTab';
import PhotoMyPhotoTypeTab from '@/components/Photo/PhotoMyPhotoTypeTab';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import mixpanel from '@/utils/mixpanel';

const TAB = {
  MY: {
    displayText: 'My Photos',
  },
  EVERYONE: {
    displayText: 'For everyone',
  },
};

const TABS = Object.values(TAB);

export default {
  mixins: [platformMixin, photoDownloadMixin],
  components: {
    BaseButton,
    BackButton,
    LoadingState,
    PhotosExpPhotosEmpty,
    PhotosModal,
    PhotosEveryone,
    Gallery,
    PhotosHeader,
    PhotoEveryoneDayTab,
    PhotoMyPhotoTypeTab,
  },
  layout: 'photos',
  data() {
    return {
      viewportWidth: null,
      selectedTab: TAB.MY,
      // tabs: TABS,
      tab: TAB,
      showModal: false,
      forEveryoneDaySlugs: forEveryoneDaySlugs,
      forEveryoneDaySlugMappings: {},
      stickyHeaderHeight: 124,
      stickyHeaderBottom: 124,
      subTabHeight: 92,
    };
  },
  computed: {
    ...mapState('photos', [
      'photos',
      'everyonePhotos',
      'isSelectingPhotos',
      'selectedPhotos',
      'isShowQr',
      'loading',
      'contentLoading',
      'selectedDayTabGPCR',
      'selectedMyPhotoTypeTabGPCR',
    ]),
    ...mapGetters('photos', ['isPhotosEmpty']),
    ...mapGetters('schedule', ['GPCRScheduleDates']),
    ...mapGetters('profile', ['isAbsentAlumni']),
    ...mapState('user', ['token']),
    isInitialLoading() {
      return this.loading && this.photos.length === 0 && this.everyonePhotos.length === 0;
    },
    isWhichDayAnchorTagPresentInTheUrl() {
      if (this.$route.hash) {
        return this.forEveryoneDaySlugs.includes(this.$route.hash.slice(1));
      }
      return false;
    },
    scheduleDatesComputed() {
      return this.GPCRScheduleDates;
    },
    tabs() {
      return TABS.map(tab => {
        return {
          displayText: tab.displayText,
          translatedText:
            tab.displayText === 'My Photos'
              ? this.$t('copy.photoDropPage.myPhotosTab.tabName')
              : this.$t('copy.photoDropPage.forEveryoneTab.tabName'),
        };
      });
    },
  },
  async ionViewWillEnter() {
    this.$store.commit('photos/setEveryonePhotos', []);
    this.populateForEveryoneDaySlugMappings();
    this.loadPhotos();
  },
  async ionViewDidEnter() {
    this.measureLayout();
    if (this.isWhichDayAnchorTagPresentInTheUrl) {
      // Switch to the For Everyone tab
      await this.onSelectTab(this.tabs[1]);
      await this.$refs.photoEveryOneDayTab.changeDayTab(this.getDateFromAnchorTag());
    }
    FirebaseAnalytics.setScreenName({
      screenName: 'photodrop - my photos',
      nameOverride: 'PhotoDropPage - My Photos',
    });
    await this.$store.dispatch('signup/fetchExpData', {
      locale: this.$i18n.locale,
      conferenceType: 'Regional',
    });
  },
  async ionViewDidLeave() {
    this.onSelectTab(this.tabs[1]);
  },
  mounted() {
    this.viewportWidth = window.innerWidth;
    window.addEventListener('resize', this.checkViewportWidth);
    this.showModal = this.isShowQr;
    this.measureLayout();
    this.$nextTick(() => {
      if (this.$refs.stickyHeader && window.ResizeObserver) {
        this._headerObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this._headerResizeFrame);
          this._headerResizeFrame = requestAnimationFrame(() => {
            this._headerResizeFrame = null;
            this.measureLayout();
          });
        });
        this._headerObserver.observe(this.$refs.stickyHeader);
      }
    });
  },
  unmounted() {
    window.removeEventListener('resize', this.checkViewportWidth);
    if (this._headerObserver) this._headerObserver.disconnect();
    cancelAnimationFrame(this._headerResizeFrame);
  },
  watch: {
    '$store.state.photos.retryTrigger'(newVal, oldVal) {
      if (newVal > oldVal && this.$store.state.photos.failedUrls.length) {
        this.downloadPhotos([...this.$store.state.photos.failedUrls], true);
      }
    },
    selectedTab() {
      this.measureLayout();
    },
    isInitialLoading(newVal) {
      if (!newVal) {
        // Sub-tabs mount when content first becomes visible — re-measure so subTabHeight is accurate
        this.$nextTick(() => this.measureLayout());
      }
    },
  },
  methods: {
    getDataEventTracking(tab) {
      if (tab.displayText === TAB.MY.displayText) {
        return 'photodrop-my-photo';
      } else if (tab.displayText === TAB.EVERYONE.displayText) {
        return 'photodrop-for-everyone';
      }
    },
    measureLayout() {
      this.$nextTick(() => {
        if (this.$refs.stickyHeader) {
          this.stickyHeaderHeight = this.$refs.stickyHeader.offsetHeight;
          this.stickyHeaderBottom = this.$refs.stickyHeader.getBoundingClientRect().bottom;
        }
        const subTab = this.$refs.photoEveryOneDayTab?.$el ?? this.$refs.PhotoMyPhotoTypeTab?.$el;
        if (subTab) this.subTabHeight = subTab.offsetHeight;
      });
    },
    checkViewportWidth() {
      this.viewportWidth = window.innerWidth;
      this.measureLayout();
    },
    back() {
      this.$router.push('/gpcr/home');
    },
    resetSelectPhotos() {
      this.$store.dispatch('photos/updateIsSelectingPhotos', false);
      this.$store.dispatch('photos/resetSelectedPhotos');
    },
    async onSelectTab(tab) {
      this.selectedTab = tab;
      this.resetSelectPhotos();
      if (this.selectedTab.displayText === 'For everyone') {
        FirebaseAnalytics.logEvent({
          name: 'click_button',
          params: {
            button_name: 'photodrop-for-everyone',
            page: 'photodrop',
          },
        });
        await this.$store.dispatch('photos/fetchPhotosEveryone', {
          tabName: this.selectedDayTabGPCR,
          conferenceType: 'Regional',
        });
        FirebaseAnalytics.setScreenName({
          screenName: 'photodrop - for everyone',
          nameOverride: 'PhotoDropPage - For Everyone',
        });
      } else if (this.selectedTab.displayText === 'My Photos') {
        FirebaseAnalytics.logEvent({
          name: 'click_button',
          params: {
            button_name: 'photodrop-my-photo',
            page: 'photodrop',
          },
        });
        FirebaseAnalytics.setScreenName({
          screenName: 'photodrop - my photos',
          nameOverride: 'PhotoDropPage - My Photos',
        });
      }
    },
    async loadPhotos() {
      if (this.selectedTab.displayText === 'For everyone') {
        await this.$store.dispatch('photos/fetchPhotosEveryone', {
          tabName: this.selectedDayTabGPCR,
          conferenceType: 'Regional',
        });
      } else {
        await this.$store.dispatch('photos/fetchPhotos', this.selectedMyPhotoTypeTabGPCR);
      }
    },
    async refreshMyPhoto() {
      this.resetSelectPhotos();
      this.loadPhotos();
    },
    closeModal() {
      this.showModal = false;
    },
    openModal() {
      this.showModal = true;
    },
    handleDownload(section) {
      if (!this.selectedPhotos.length) return;
      this._trackDownload(this.selectedPhotos, section);
      this.downloadPhotos(this._resolveSelectedForDownload(this.selectedPhotos));
    },
    handleDownloadAll() {
      const photos = this.$store.state.photos.everyonePhotos;
      try {
        mixpanel.track('Photodrop Download All', {
          page_url: this.$route.path,
          photo_section: 'for_everyone',
          file_quantity: photos.length,
          page: 'photodrop',
        });
      } catch (e) {
        // noop
      }
      this.downloadAllPhotos(this.selectedDayTabGPCR, 'Regional');
    },
    _trackDownload(urls, section) {
      const isEveryone = section === 'everyone';
      urls.forEach(url => {
        try {
          const result = new URL(url).pathname;
          FirebaseAnalytics.logEvent({
            name: isEveryone ? 'file_downloaded_for_everyone' : 'file_downloaded_my_photos',
            params: { file_name: result },
          });
        } catch (e) {
          /* noop */
        }
        try {
          window.dataLayer.push({
            event: isEveryone
              ? 'file-downloaded-web-for-everyone'
              : 'file-downloaded-web-my-photos',
            file_name: new URL(url).pathname,
          });
        } catch (e) {
          /* noop */
        }
      });
      try {
        FirebaseAnalytics.logEvent({
          name: 'click_button',
          params: {
            button_name: isEveryone
              ? 'photodrop-download-button-for-everyone'
              : 'photodrop-download-button-my-photo',
            page: 'photodrop',
            file_quantity: urls.length,
          },
        });
      } catch (e) {
        /* noop */
      }
      try {
        window.dataLayer.push({
          event: isEveryone
            ? 'photodrop-download-web-for-everyone'
            : 'photodrop-download-web-my-photos',
          file_quantity: urls.length,
          page_section: isEveryone ? 'for-everyone' : 'my-photos',
        });
      } catch (e) {
        /* noop */
      }
      try {
        mixpanel.track('Photodrop Bulk Download', {
          page_url: this.$route.path,
          download_type: 'direct',
          photo_section: isEveryone ? 'for_everyone' : 'my_photos',
          file_quantity: urls.length,
          page: 'photodrop',
        });
      } catch (e) {
        /* noop */
      }
    },
    populateForEveryoneDaySlugMappings() {
      this.forEveryoneDaySlugs.forEach((slug, index) => {
        if (this.scheduleDatesComputed[index]) {
          this.forEveryoneDaySlugMappings[slug] = this.scheduleDatesComputed[index];
        } else {
          this.forEveryoneDaySlugMappings[slug] = 'Featured';
        }
      });
    },
    getDateFromAnchorTag() {
      const hash = this.$route.hash.slice(1);
      return this.forEveryoneDaySlugMappings[hash];
    },
  },
};
</script>

<style>
.qr-container {
  /* background-image: linear-gradient(to bottom right, #FFD900 12%,#FFD900, #FFD900, #FFD900); */
  background-color: #ffd900;
}

.tab-card {
  box-shadow: 0px -3px 20px rgba(0, 0, 0, 0.08);
}

ion-refresher.refresher-native {
  display: block;
  z-index: 100;
}
</style>
